import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, CheckCircle, Building2, User, Mail, Phone, Briefcase, Globe, Upload, FileText, Trash2, MessageSquare, ChevronDown, ChevronUp, MapPin, Users as UsersIcon, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import axios from 'axios';
import { assessmentRequest, pollAssessment } from '../../utils/assessmentProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Industry sectors with industry-specific KPIs (alphabetically sorted)
const sectors = [
  'Aerospace & Defense',
  'Agriculture',
  'Automotive',
  'Biotechnology',
  'Chemicals',
  'Consumer Packaged Goods',
  'Cosmetics & Personal Care',
  'Education',
  'Energy',
  'EPC (Engineering, Procurement & Construction)',
  'Financial Services',
  'Food Processing',
  'Healthcare',
  'Insurance',
  'Manufacturing',
  'Media & Entertainment',
  'Medical Devices',
  'Metals & Mining',
  'Nonprofit',
  'Oil & Gas',
  'Pharmaceuticals',
  'Private Equity',
  'Professional Services',
  'Pulp & Paper',
  'Real Estate & Construction',
  'Restaurant',
  'Retail',
  'Social Sector & NGOs',
  'Software',
  'Technology',
  'Telecommunications',
  'Travel & Logistics',
  'Utilities',
  'Waste Management',
  'Wine, Beer & Spirits',
];

// Region / geography options (used for peer selection + regional benchmarking)
const regions = [
  'North America',
  'Latin America',
  'Europe (EU + UK)',
  'Middle East & Africa (MEA)',
  'India',
  'APAC (ex-India)',
  'Global / Multi-region',
];

// Company size buckets (by headcount)
const companySizes = [
  'Micro (1–49 employees)',
  'Small (50–249 employees)',
  'Mid-market (250–999 employees)',
  'Large (1,000–4,999 employees)',
  'Enterprise (5,000–19,999 employees)',
  'Mega-cap (20,000+ employees)',
];

// Annual revenue bands (USD)
const annualRevenues = [
  'Under $1M',
  '$1M – $10M',
  '$10M – $50M',
  '$50M – $250M',
  '$250M – $1B',
  '$1B – $10B',
  '$10B+',
  'Prefer not to disclose',
];

// Collapsible Section Component - defined outside to prevent re-renders
const CollapsibleSection = ({ title, icon: Icon, isOpen, onToggle, badge, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
        <span className="font-medium text-gray-700 text-sm sm:text-base">{title}</span>
        {badge && (
          <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>
      {isOpen ? (
        <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
      ) : (
        <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
      )}
    </button>
    {isOpen && <div className="p-3 sm:p-4 border-t border-gray-200">{children}</div>}
  </div>
);

const FreeTrialModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  // Collapsible sections state
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [showPeerSection, setShowPeerSection] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    sector: '',
    company_name: '',
    company_url: '',
    region: '',
    company_size: '',
    annual_revenue: '',
    peer1: '',
    peer2: '',
    peer3: '',
    custom_focus: '',
    currency: 'USD',
    assessment_period: 'Latest available',
  });

  const allowedTypes = ['pdf', 'xlsx', 'xls', 'csv', 'txt', 'docx'];
  const maxFileSize = 20 * 1024 * 1024; // 20MB

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSectorChange = (value) => {
    setFormData({ ...formData, sector: value });
    setError('');
  };

  const handleSelectChange = (field) => (value) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
  };

  const addFiles = (files) => {
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      if (!allowedTypes.includes(ext)) {
        setError(`${file.name}: File type not supported`);
        return false;
      }
      if (file.size > maxFileSize) {
        setError(`${file.name}: File too large (max 20MB)`);
        return false;
      }
      return true;
    });

    setUploadedFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
    setError('');
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    addFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) return 'Please enter your name';
    if (!formData.email.trim()) return 'Please enter your email';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    if (!formData.phone.trim()) return 'Please enter your phone number';
    return null;
  };

  const validateStep2 = () => {
    if (!formData.sector) return 'Please select an industry sector';
    if (!formData.region) return 'Please select a region';
    if (!formData.company_size) return 'Please select company size';
    if (!formData.annual_revenue) return 'Please select annual revenue band';
    if (!formData.company_name.trim()) return 'Please enter your company name';
    return null;
  };

  const handleNext = () => {
    const error = validateStep1();
    if (error) {
      setError(error);
      return;
    }
    setStep(2);
  };

  const pollForAnalysis = (email, userId) => pollAssessment(API, email, userId, setLoadingMessage);

  const completeSignup = (userId) => {
    sessionStorage.removeItem('samanvai_pending_assessment');
    localStorage.removeItem('samanvai_verified');
    localStorage.setItem('samanvai_user_id', userId);
    localStorage.setItem('samanvai_user_name', formData.name);
    localStorage.setItem('samanvai_company', formData.company_name);
    setStep(3);
    setTimeout(() => {
      onClose();
      navigate('/dashboard');
    }, 2000);
  };

  const handleSubmit = async () => {
    const validationError = validateStep2();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');
    setLoadingMessage('Uploading data...');
    let progressTimer;

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('sector', formData.sector);
      submitData.append('company_name', formData.company_name);
      submitData.append('company_url', formData.company_url || '');
      submitData.append('region', formData.region || '');
      submitData.append('company_size', formData.company_size || '');
      submitData.append('annual_revenue', formData.annual_revenue || '');
      submitData.append('peer1', formData.peer1 || '');
      submitData.append('peer2', formData.peer2 || '');
      submitData.append('peer3', formData.peer3 || '');
      submitData.append('custom_focus', formData.custom_focus || '');
      submitData.append('currency', formData.currency || 'USD');
      submitData.append('assessment_period', formData.assessment_period || 'Latest available');
      submitData.append('request_id', assessmentRequest(formData, uploadedFiles).requestId);
      
      // Append all uploaded files
      uploadedFiles.forEach((file, index) => {
        submitData.append('files', file);
      });

      // Start a progress timer
      setLoadingMessage('Submitting assessment...');

      const response = await axios.post(`${API}/trial/signup-with-reports`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // signup returns fast — generation continues server-side in the background
      });

      if (response.data.success) {
        // Backend returns immediately; the scorecard generates in the background.
        // Keep the progress messages cycling while we poll for completion.
        try {
          const data = await pollForAnalysis(formData.email, response.data.user_id);
          clearInterval(progressTimer);
          setLoadingMessage('');
          completeSignup(response.data.user_id || data.user_id);
        } catch (pollErr) {
          if (pollErr.terminal) sessionStorage.removeItem('samanvai_pending_assessment');
          clearInterval(progressTimer);
          setLoadingMessage('');
          setError(pollErr.message || 'Analysis failed. Please try again.');
        }
        return;
      }
    } catch (err) {
      clearInterval(progressTimer);
      console.error('Signup error:', err);
      console.error('Response status:', err.response?.status);
      console.error('Response data:', err.response?.data);

      // Long analyses can outlive the HTTP proxy window (~60s). The server keeps
      // working — recover by polling the analysis status instead of failing.
      // Covers ECONNABORTED, connection resets and ALL 5xx gateway statuses
      // (502/504/520/522/524 etc. from the edge proxy).
      const connectionCut = err.code === 'ECONNABORTED' || !err.response || err.response.status >= 500;
      if (connectionCut) {
        setLoadingMessage('Still working — deep analyses can take a few minutes...');
        try {
          const data = await pollForAnalysis(formData.email);
          clearInterval(progressTimer);
          setLoadingMessage('');
          completeSignup(data.user_id);
          return;
        } catch (pollErr) {
          if (pollErr.terminal) sessionStorage.removeItem('samanvai_pending_assessment');
          clearInterval(progressTimer);
          setError(pollErr.message || 'Analysis failed. Please try again.');
          setLoadingMessage('');
          setLoading(false);
          return;
        }
      }

      let errorMessage = 'Failed to create trial. Please try again.';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.status === 413) {
        errorMessage = 'File is too large. Please upload a file smaller than 10MB.';
      } else if (err.response?.status === 502 || err.response?.status === 504) {
        errorMessage = 'Server is temporarily busy processing. Please try again in a moment.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoadingMessage('');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      name: '',
      email: '',
      phone: '',
      sector: '',
      company_name: '',
      company_url: '',
      region: '',
      company_size: '',
      annual_revenue: '',
      currency: 'USD',
      assessment_period: 'Latest available',
      peer1: '',
      peer2: '',
      peer3: '',
    });
    setUploadedFiles([]);
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="fixed inset-0 z-50 w-screen h-screen max-w-none max-h-none translate-x-0 translate-y-0 left-0 top-0 p-0 overflow-y-auto rounded-none border-none gap-0" data-testid="free-trial-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 sm:p-6 text-white sticky top-0 z-10">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-2xl font-bold text-white">
              {step === 1 && "Let's Get Started"}
              {step === 2 && 'Tell Us About Your Company'}
              {step === 3 && 'Welcome to SAMANVAI!'}
            </DialogTitle>
            <DialogDescription className="text-emerald-100 text-sm sm:text-base">
              {step === 1 && 'Enter your details to get started'}
              {step === 2 && 'Company information & optional report upload'}
              {step === 3 && 'Your analysis is ready'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Progress indicator */}
          {step !== 3 && (
            <div className="flex gap-2 mt-3 sm:mt-4">
              <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-white' : 'bg-white/30'}`} />
              <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex justify-center">
          <div className="w-full max-w-2xl p-6 sm:p-10">
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="pl-10 h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Work Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="pl-10 h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="pl-10 h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Company Info with URL and File Upload */}
          {step === 2 && (
            <div className="space-y-3 sm:space-y-4">
              {/* Required Fields — 2×2 grid: Industry / Region · Company Size / Annual Revenue */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Industry *</label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                    <Select value={formData.sector} onValueChange={handleSectorChange}>
                      <SelectTrigger className="pl-10 h-11 sm:h-10 text-base sm:text-sm" data-testid="select-sector">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map((sector) => (
                          <SelectItem key={sector} value={sector}>
                            {sector}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Region *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                    <Select value={formData.region} onValueChange={handleSelectChange('region')}>
                      <SelectTrigger className="pl-10 h-11 sm:h-10 text-base sm:text-sm" data-testid="select-region">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Company Size *</label>
                  <div className="relative">
                    <UsersIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                    <Select value={formData.company_size} onValueChange={handleSelectChange('company_size')}>
                      <SelectTrigger className="pl-10 h-11 sm:h-10 text-base sm:text-sm" data-testid="select-company-size">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        {companySizes.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Annual Revenue *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                    <Select value={formData.annual_revenue} onValueChange={handleSelectChange('annual_revenue')}>
                      <SelectTrigger className="pl-10 h-11 sm:h-10 text-base sm:text-sm" data-testid="select-annual-revenue">
                        <SelectValue placeholder="Select revenue" />
                      </SelectTrigger>
                      <SelectContent>
                        {annualRevenues.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Reporting Currency</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                    <Select value={formData.currency} onValueChange={handleSelectChange('currency')}>
                      <SelectTrigger className="pl-10 h-11 sm:h-10 text-base sm:text-sm" data-testid="select-currency">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD — US Dollar ($)</SelectItem>
                        <SelectItem value="INR">INR — Indian Rupee (₹)</SelectItem>
                        <SelectItem value="EUR">EUR — Euro (€)</SelectItem>
                        <SelectItem value="GBP">GBP — British Pound (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">All values in your scorecard will use this currency, with the FX basis stated.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Assessment Period</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                    <Select value={formData.assessment_period} onValueChange={handleSelectChange('assessment_period')}>
                      <SelectTrigger className="pl-10 h-11 sm:h-10 text-base sm:text-sm" data-testid="select-assessment-period">
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Latest available">Latest available (recommended)</SelectItem>
                        <SelectItem value="FY2025-26">FY2025-26 (Apr 2025 – Mar 2026)</SelectItem>
                        <SelectItem value="FY2024-25">FY2024-25 (Apr 2024 – Mar 2025)</SelectItem>
                        <SelectItem value="CY2025">Calendar Year 2025</SelectItem>
                        <SelectItem value="CY2024">Calendar Year 2024</SelectItem>
                        <SelectItem value="Trailing twelve months">Trailing twelve months (TTM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">The report anchors to this period and states the exact filings used — no ambiguity on data currency.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Company Name *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Enter company for performance analysis"
                    className="pl-10 h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Company Website URL</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="company_url"
                    value={formData.company_url}
                    onChange={handleChange}
                    placeholder="https://www.company.com"
                    className="pl-10 h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-url"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Optional - helps us gather more accurate data</p>
              </div>

              {/* Custom Focus Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-emerald-600" />
                    What would you like to focus on?
                  </span>
                </label>
                <Textarea
                  name="custom_focus"
                  value={formData.custom_focus}
                  onChange={handleChange}
                  placeholder="E.g., supply chain efficiency, ESG metrics, digital transformation..."
                  className="min-h-[80px] sm:min-h-[100px] resize-none text-base sm:text-sm"
                  data-testid="textarea-focus"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your input will help tailor the analysis to your needs
                </p>
              </div>

              {/* Collapsible: File Upload Section */}
              <CollapsibleSection
                title="Upload Company Reports"
                icon={Upload}
                isOpen={showUploadSection}
                onToggle={() => setShowUploadSection(!showUploadSection)}
                badge={uploadedFiles.length > 0 ? `${uploadedFiles.length} files` : 'Optional'}
              >
                <div
                  className={`border-2 border-dashed rounded-xl p-3 sm:p-4 text-center transition-colors cursor-pointer ${
                    uploadedFiles.length > 0 ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-emerald-400 hover:bg-gray-50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf,.xlsx,.xls,.csv,.txt,.docx"
                    multiple
                    className="hidden"
                    data-testid="input-file"
                  />
                  
                  <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Tap to select files</p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, Excel, CSV, Word (max 20MB)
                  </p>
                </div>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">({formatFileSize(file.size)})</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="p-2 hover:bg-gray-200 rounded ml-2"
                          data-testid={`remove-file-${index}`}
                        >
                          <Trash2 className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CollapsibleSection>

              {/* Collapsible: Peer Comparison Section */}
              <CollapsibleSection
                title="Compare with Peers"
                icon={Building2}
                isOpen={showPeerSection}
                onToggle={() => setShowPeerSection(!showPeerSection)}
                badge={(formData.peer1 || formData.peer2 || formData.peer3) ? 'Custom' : 'Auto-select'}
              >
                <p className="text-xs text-gray-500 mb-3">
                  Add up to 3 competitors, or leave empty for AI auto-selection
                </p>
                <div className="space-y-2">
                  <Input
                    name="peer1"
                    value={formData.peer1}
                    onChange={handleChange}
                    placeholder="Competitor 1"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-peer1"
                  />
                  <Input
                    name="peer2"
                    value={formData.peer2}
                    onChange={handleChange}
                    placeholder="Competitor 2"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-peer2"
                  />
                  <Input
                    name="peer3"
                    value={formData.peer3}
                    onChange={handleChange}
                    placeholder="Competitor 3"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                    data-testid="input-peer3"
                  />
                </div>
                {!(formData.peer1 || formData.peer2 || formData.peer3) && (
                  <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    AI will auto-select top 3 competitors
                  </p>
                )}
              </CollapsibleSection>

              {/* What you'll get - Compact */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 sm:p-4">
                <h4 className="font-medium text-emerald-800 mb-2 text-sm sm:text-base">What you will get:</h4>
                <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs sm:text-sm text-emerald-700">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    <span>Top 10 KPIs</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    <span>Industry Benchmarks</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    <span>Impact Analysis</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    <span>5-Year Roadmap</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center py-6 sm:py-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Analysis Complete!</h3>
              <p className="text-gray-600 text-sm sm:text-base mb-2">
                We have analyzed <strong>{formData.company_name}</strong>.
              </p>
              <p className="text-gray-600 text-sm mb-4">
                A secret access code has been sent to <strong>{formData.email}</strong>.
              </p>
              <p className="text-sm text-gray-500">Redirecting to your sample preview...</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div data-testid="assessment-error" role="alert" className="mt-3 sm:mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          {step !== 3 && (
            <div className="flex gap-3 mt-4 sm:mt-6">
              {step === 2 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 h-12 sm:h-10 text-base sm:text-sm"
                  disabled={loading}
                  data-testid="btn-back"
                >
                  Back
                </Button>
              )}
              
              {step === 1 && (
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 sm:h-10 text-base sm:text-sm"
                  data-testid="btn-next"
                >
                  Next
                </Button>
              )}
              
              {step === 2 && (
                <Button
                  onClick={handleSubmit}
                  className="flex-1 min-w-0 bg-emerald-600 hover:bg-emerald-700 min-h-12 h-auto py-2 text-sm whitespace-normal"
                  disabled={loading}
                  data-testid="btn-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      <span data-testid="assessment-progress" role="status">{loadingMessage || 'Analyzing...'}</span>
                    </>
                  ) : (
                    'Generate Analysis'
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FreeTrialModal;
